/**
 * Promo banner - Figma `Block/Sustainable` (`6377:86970`, and `6377:87272`,
 * `6377:87422`, `6377:87465`, `6377:88746`).
 *
 * A photograph with a white card inset over its left half. Inside the card the eyebrow
 * and heading sit at the top and the body and call to action at the bottom, so the
 * whitespace between them scales with the banner rather than collecting underneath.
 *
 * `variant` picks the height: `tall` is the home-page treatment at 682px, `compact` the
 * 401px band further down a page (`3883:71239`). Both heights are minimums: a long Polish
 * heading grows the card rather than being clipped.
 *
 * **The heading is h4 in every variant** (2026-09-26, the designer's call). The file had
 * three sizes for one block - h2 on Home (6377:86970), h3 on Spotkania (6377:87422), h4 on
 * the compact band (6377:87272) - and the h2 was what made the compact band 530px tall
 * before `variant` existed.
 *
 * Without an image the card is the whole section, which keeps the section usable while
 * an editor is still waiting on photography.
 *
 * **`partner-feature` was merged into this on 2026-09-04.** It drew the same card inset
 * over a photograph, at the compact height and with no call to action - so it is this
 * section with `variant: compact` and two fields left empty. Its body was rich text and
 * this one's was plain, so `body` is rich text now.
 *
 * The two nodes disagree on one thing and a designer should settle it: `6377:86970` gives
 * the band and the card a 28px radius, `6377:88269` gives them 14 and 8. This draws the
 * promo banner's, because that node is used five times over and the partner band twice.
 *
 * **A `logo` makes it the partner band** (6377:88269, 2026-11-01): the card beside a
 * frosted-glass panel carrying the logo, an SVG if the editor uploads one. The glass is
 * CSS, so it works over any photograph; see the comment on the fields in the spec.
 *
 * **`square` is a third drawing** (6377:89290, 2026-09-26): a 16:9 photograph, 1360x765 in
 * the file, with a 485px square card 16 in from its bottom corner, radius 14 and 8 - the
 * partner band's. It is the band closing Business Travel's panel block (3883:87965).
 *
 * In all three the body is body-medium and pinned to the foot of the card with the button,
 * `mt-auto` rather than relying on `justify-between`, which drops it to the top whenever
 * the card has no heading group above it.
 */
import { Blocks, CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import type { PromoBannerSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';
import { MediaFrame, hasMedia } from '../../components/ui/MediaFrame';

export default function PromoBanner({ data }: { data: PromoBannerSection }) {
  const image = hasMedia(data.media);
  const tall = (data.variant ?? 'tall') === 'tall';
  const square = data.variant === 'square';
  const right = data.cardSide === 'right';

  const content = (
    <>
      <div className="flex flex-col gap-6">
        {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
        <h2 className="text-h4 text-ink-strong">
          {withHighlight(data.heading)}
        </h2>
      </div>

      <div className="mt-auto flex flex-col gap-6">
        {data.body ? (
          <Blocks value={data.body} className="max-w-2xl text-body-md text-ink-strong [&>p:last-child]:mb-0" />
        ) : null}
        {data.cta ? <CtaLink value={data.cta} /> : null}
      </div>
    </>
  );

  if (square) {
    // Below lg the photograph sits above the card rather than behind it: on a phone the
    // card's text is taller than the band is wide, and laid over the picture it hid all
    // of it but the 16px frame.
    return (
      <SectionShell settings={data.settings} name="promo-banner">
        <div className={`relative flex flex-col overflow-hidden rounded-l bg-surface-sunken lg:aspect-[16/9] lg:justify-end lg:p-4 ${
          right ? 'lg:items-end' : 'lg:items-start'
        }`}>
          {image ? (
            <div className="relative aspect-[16/9] lg:absolute lg:inset-0 lg:aspect-auto">
              <div className="absolute inset-0">
                <MediaFrame media={data.media} sizes={SIZES.full} title={data.heading} />
              </div>
            </div>
          ) : null}
          <div className="relative m-4 flex flex-col gap-10 rounded-s bg-surface p-8 lg:m-0 lg:aspect-square lg:w-[485px]">
            {content}
          </div>
        </div>
      </SectionShell>
    );
  }

  // A logo makes it the partner band (6377:88269): the card on one half, a frosted-glass
  // panel with the logo on the other, and that node's radii - 14 on the band, 8 on the
  // card - where the other three draw 28. The panel is `backdrop-filter` over whatever
  // sits behind the banner, not an image, so any photograph can go there. Figma's blur 14
  // is CSS 7px: Figma's radius is twice CSS's standard deviation.
  const logo = data.logo;
  const partner = Boolean(logo);
  const bandRadius = partner ? 'rounded-l' : 'rounded-xxxl';
  const cardRadius = partner ? 'rounded-s' : 'rounded-xxxl';

  // Over a photograph the card takes the left half, as in the design. With no photo
  // there is nothing to leave room for, so it takes the width.
  const card = (
    <div
      className={`relative flex flex-col justify-between gap-16 ${cardRadius} bg-surface p-8 ${
        image && !partner ? `lg:w-[49%] ${right ? 'lg:ml-auto' : ''}` : 'w-full'
      } ${tall ? 'lg:min-h-[650px]' : 'lg:min-h-[369px]'}`}
    >
      {content}
    </div>
  );

  const body = partner ? (
    <div className="relative grid gap-4 lg:grid-cols-2">
      {card}
      <div
        className={`flex min-h-[160px] items-center justify-center ${cardRadius} border border-white/20 bg-white/10 p-8 backdrop-blur-[7px] ${
          right ? 'lg:order-first' : ''
        }`}
      >
        {logo ? (
          <Image
            media={logo}
            alt={logo.alternativeText ?? ''}
            sizes="400px"
            className="max-h-20 w-auto max-w-[70%] object-contain"
          />
        ) : null}
      </div>
    </div>
  ) : card;

  return (
    <SectionShell settings={data.settings} name="promo-banner">
      {image ? (
        <div className={`relative overflow-hidden ${bandRadius} bg-surface-sunken p-4 ${tall ? 'min-h-[682px]' : 'min-h-[400px]'}`}>
          <div className="absolute inset-0">
            <MediaFrame media={data.media} sizes={SIZES.full} title={data.heading} />
          </div>
          {body}
        </div>
      ) : (
        <div className={`${bandRadius} bg-surface-muted p-4`}>{body}</div>
      )}
    </SectionShell>
  );
}
